import React, { Fragment, useMemo } from "react";

import { faStar } from "@fortawesome/pro-solid-svg-icons";
import { faCheckCircle } from "@fortawesome/pro-solid-svg-icons/faCheckCircle";
import { faStopwatch } from "@fortawesome/pro-solid-svg-icons/faStopwatch";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared";
import User from "@mapmycustomers/shared/types/User";
import formatFileSize from "@mapmycustomers/shared/util/formatters/formatFileSize";
import { Avatar, AvatarWithName } from "@mapmycustomers/ui";

import EntityBadge from "@app/component/EntityBadge";
import styles from "@app/component/GlobalSearch/components/Item/Title.module.scss";
import TextHighlighter from "@app/component/TextHighlighter";
import UserPreviewCard from "@app/component/UserPreviewCard";
import GlobalSearchItem from "@app/types/globalSearch/GlobalSearchItem";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import { formatDate, formatRawDate, getUserDisplayNameWithInfo } from "@app/util/formatters";
import { parseApiDate } from "@app/util/parsers";
import getShortAddress from "@app/util/ui/getShortAddress";

const useSecondaryParts = (item: GlobalSearchItem, users: User[], activeQuery: string) => {
  const intl = useIntl();

  const addressField = item.entityType
    ? // we're fine to get undefined here if item.entityType is not among those
      // understood by getFieldModelByEntityType
      // @ts-ignore
      getFieldModelByEntityType(item.entityType)?.getByName("address")
    : undefined;
  const isAddressReadable = addressField?.isReadable ?? false;

  return useMemo(() => {
    let parts: React.ReactNode[] = [];
    const entity = item.entity;

    if (!entity) {
      return null;
    }

    switch (entity.entity) {
      case EntityType.COMPANY:
        if (isAddressReadable) {
          parts.push(getShortAddress(entity));
        }
        parts.push(entity.phone ?? "");
        parts.push(entity.email ?? "");
        break;
      case EntityType.PERSON:
        if (isAddressReadable) {
          parts.push(getShortAddress(entity));
        }
        parts.push(
          entity.primaryAccount ? (
            <span className={styles.primaryCompany}>
              <FontAwesomeIcon className={styles.star} icon={faStar} />
              {entity.primaryAccount.name}
            </span>
          ) : (
            ""
          )
        );
        parts.push(entity.phone ?? "");
        parts.push(entity.email ?? "");
        break;
      case EntityType.DEAL:
        if (entity.stage) {
          parts.push(<EntityBadge entity={entity.stage} />);
        }
        parts.push(entity.funnel?.name ?? "");
        parts.push(entity.account?.name ?? "");
        parts.push(entity.contact?.name ?? "");
        break;
      case EntityType.DEAL_GROUP:
      case EntityType.PEOPLE_GROUP:
      case EntityType.COMPANY_GROUP:
        {
          if (entity.cadenceInterval) {
            parts.push(
              <span className={styles.cadenceValue}>
                <FontAwesomeIcon icon={faStopwatch} />
                {intl.formatMessage(
                  {
                    id: "component.globalSearch.item.group.cadenceInterval",
                    defaultMessage: "Every {count} {count, plural, one {day} other {days}}",
                    description: "Cadence details interval row value title on global search item",
                  },
                  { count: entity.cadenceInterval }
                )}
              </span>
            );
          }
          if (entity.user) {
            parts.push(
              <UserPreviewCard userId={entity.user.id}>
                <div>
                  <AvatarWithName
                    actionOnHover
                    avatarClassName={styles.userAvatar}
                    user={entity.user}
                  />
                </div>
              </UserPreviewCard>
            );
          }
        }
        break;
      case EntityType.ACTIVITY:
        parts.push(entity.name);
        if (entity.startAt) {
          parts.push(formatDate(parseApiDate(entity.startAt), "PPP"));
        }
        if (entity.completed) {
          parts.push(
            <span className={styles.activityDone}>
              <FontAwesomeIcon icon={faCheckCircle} />
              <span>
                {intl.formatMessage({
                  id: "component.globalSearch.item.activity.done",
                  defaultMessage: "Done",
                  description: "Activity done on global search item",
                })}
              </span>
            </span>
          );
        }
        break;
      case EntityType.TERRITORY:
        {
          const userIds = new Set(entity.userIds);
          const territoryUsers = users.filter(({ id }) => userIds.has(id));
          const firstUsers = territoryUsers.slice(0, 3);
          const restUsers = territoryUsers.slice(3);
          parts.push(
            <span className={styles.territoryUsers}>
              {firstUsers.map((user) => (
                <UserPreviewCard key={user.id} userId={user.id}>
                  <Avatar actionOnHover className={styles.userAvatar} user={user} />
                </UserPreviewCard>
              ))}
              {restUsers.length ? (
                <Tooltip
                  title={restUsers.map((user) => getUserDisplayNameWithInfo(intl, user)).join(", ")}
                >
                  {intl.formatMessage(
                    {
                      id: "component.globalSearch.item.territory.more",
                      defaultMessage: "+ {count} more...",
                      description: "Territory users more on global search item",
                    },
                    { count: restUsers.length }
                  )}
                </Tooltip>
              ) : null}
            </span>
          );
        }
        break;
      case EntityType.ROUTE:
      case EntityType.COMPANY_ROUTE:
      case EntityType.PEOPLE_ROUTE:
        parts.push(
          intl.formatMessage(
            {
              id: "component.globalSearch.item.routes.stops",
              defaultMessage: "{count} {count, plural, one {Stop} other {Stops}}",
              description: "Route stops count on global search item",
            },
            { count: entity.items }
          )
        );
        parts.push(
          getShortAddress(entity.routeDetail.geoAddress ?? undefined) ||
            entity.routeDetail.startAddress
        );
        break;
      case EntityType.RESOURCE:
        if (entity.url) {
          parts.push(entity.url.substring(0, 70));
        } else {
          parts.push(
            formatRawDate(entity.updatedAt, "PP"),
            entity.file?.contentLength ? formatFileSize(entity.file.contentLength) : ""
          );
        }
        break;
      case EntityType.USER:
        if (entity.teams?.length) {
          parts.push(
            <span className={styles.teams}>{entity.teams.map(({ name }) => name).join(", ")}</span>
          );
        }
        if (entity.fullName?.trim()) {
          parts.push(<TextHighlighter searchText={activeQuery} text={entity.username} />);
        }
        break;
    }

    parts = parts.filter((part) => (part?.toString().trim().length ?? 0) > 0);
    return parts.map((part, i) => (
      <Fragment key={i}>
        {part}
        {i < parts.length - 1 ? <span className={styles.partsSeparator}>•</span> : ""}
      </Fragment>
    ));
  }, [intl, isAddressReadable, item.entity, users, activeQuery]);
};

export default useSecondaryParts;
